import { Check, Image, Loader2, Plus, Tag, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDarkMode } from '@/features/sistema/hooks/useDarkMode';

import { useFotoTags } from '../hooks/useFotoTags';
import { adicionarTagFoto, buscarTagsObra, excluirTagCatalogo, TagCatalogo } from '../services/fotoTagsService';

interface FotoInfo {
  id: string;
  url?: string;
}

interface FotoTagsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  // Suporte a foto única ou múltiplas
  fotoId?: string;
  fotos?: FotoInfo[];
  obraId: string;
  usuarioId?: string;
  // Posição do popover (coordenadas da tela) - ignorado no mobile
  position?: { x: number; y: number };
  // Thumbnail da foto para contexto visual (modo foto única)
  fotoUrl?: string;
}

export function FotoTagsPopover({
  isOpen,
  onClose,
  fotoId,
  fotos,
  obraId,
  usuarioId,
  position,
  fotoUrl
}: FotoTagsPopoverProps) {
  const [isDarkMode] = useDarkMode();
  const [inputValue, setInputValue] = useState('');
  const [tagsDisponiveis, setTagsDisponiveis] = useState<TagCatalogo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAcao, setLoadingAcao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tagParaExcluir, setTagParaExcluir] = useState<TagCatalogo | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputTouchedRef = useRef(false);

  // Determina se é modo múltiplas fotos
  const modoMultiplo = fotos && fotos.length > 0;
  const fotoAtual = modoMultiplo ? undefined : fotoId;
  const fotosParaTaggear = modoMultiplo ? fotos : fotoId ? [{ id: fotoId, url: fotoUrl }] : [];

  // Hook para foto única (se aplicável)
  const {
    tagsDisponiveis: tagsDisponiveisFoto,
    tagsFoto,
    loading: loadingFoto,
    loadingAcao: loadingAcaoFoto,
    adicionar: adicionarFoto,
    remover: removerFoto,
    pesquisar: pesquisarFoto,
    temTag
  } = useFotoTags({ obraId, fotoId: fotoAtual, autoCarregar: isOpen && !!fotoAtual });

  // Carrega tags disponíveis da obra para modo múltiplo
  const carregarTagsObra = useCallback(async (busca?: string) => {
    if (!obraId) return;
    try {
      setLoading(true);
      const tags = await buscarTagsObra(obraId, busca, 30);
      setTagsDisponiveis(tags);
    } catch (err) {
      console.error('Erro ao carregar tags:', err);
      setErro('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  }, [obraId]);

  // Carrega tags iniciais
  useEffect(() => {
    if (isOpen && modoMultiplo) {
      carregarTagsObra();
    }
  }, [isOpen, modoMultiplo, carregarTagsObra]);

  // Foca no input quando abre e reseta o flag de busca
  useEffect(() => {
    if (isOpen) {
      inputTouchedRef.current = false;
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [isOpen]);

  // Pesquisa com debounce (só dispara quando o usuário digita, não no mount)
  useEffect(() => {
    if (!inputTouchedRef.current) return;

    const timer = setTimeout(() => {
      if (modoMultiplo) {
        carregarTagsObra(inputValue || undefined);
      } else {
        pesquisarFoto(inputValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, modoMultiplo, carregarTagsObra, pesquisarFoto]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Fecha com ESC
  useEffect(() => {
    if (!isOpen) return;

    function handleEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Detecta mobile
  const isMobile = window.innerWidth < 640;

  const handleToggleTag = async (tagId: string, tagNome: string) => {
    if (modoMultiplo) {
      // Adiciona a tag em todas as fotos selecionadas
      setLoadingAcao(true);
      try {
        // Executa sequencialmente para evitar conflito de chave duplicada
        for (const foto of fotosParaTaggear) {
          try {
            await adicionarTagFoto(foto.id, tagNome, obraId, usuarioId);
          } catch (err: any) {
            // Ignora erro de chave duplicada (23505) - a tag já existe na foto
            if (err?.code !== '23505') {
              console.error('Erro ao adicionar tag à foto:', err);
            }
          }
        }
        // Recarrega tags
        await carregarTagsObra(inputValue || undefined);
      } catch (err) {
        console.error('Erro ao adicionar tag:', err);
        setErro('Erro ao adicionar tag');
      } finally {
        setLoadingAcao(false);
      }
    } else {
      // Modo foto única - toggle
      if (temTag(tagId)) {
        await removerFoto(tagId);
      } else {
        await adicionarFoto(tagNome, usuarioId);
      }
    }
  };

  const handleCriarTag = async () => {
    if (!inputValue.trim()) return;

    if (modoMultiplo) {
      setLoadingAcao(true);
      let sucessos = 0;
      try {
        // Executa sequencialmente para evitar conflito de chave duplicada
        // A primeira chamada cria a tag, as seguintes apenas associam
        for (const foto of fotosParaTaggear) {
          try {
            await adicionarTagFoto(foto.id, inputValue.trim(), obraId, usuarioId);
            sucessos++;
          } catch (err: any) {
            // Ignora erro de chave duplicada (23505) - a tag já existe, o que é OK
            if (err?.code !== '23505') {
              console.error('Erro ao adicionar tag à foto:', err);
            }
          }
        }
        if (sucessos > 0 || fotosParaTaggear.length > 0) {
          setInputValue('');
          await carregarTagsObra();
        }
      } catch (err) {
        console.error('Erro ao criar tag:', err);
        setErro('Erro ao criar tag');
      } finally {
        setLoadingAcao(false);
      }
    } else {
      const sucesso = await adicionarFoto(inputValue.trim(), usuarioId);
      if (sucesso) {
        setInputValue('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleCriarTag();
    }
  };

  // Excluir tag do catálogo
  const handleExcluirTag = async () => {
    if (!tagParaExcluir) return;

    setExcluindo(true);
    try {
      await excluirTagCatalogo(tagParaExcluir.id);
      setTagParaExcluir(null);
      // Recarrega a lista de tags
      await carregarTagsObra(inputValue || undefined);
    } catch (err) {
      console.error('Erro ao excluir tag:', err);
      setErro('Erro ao excluir tag');
    } finally {
      setExcluindo(false);
    }
  };

  // Tags a exibir (do hook para foto única, ou do estado local para múltiplas)
  const tagsParaExibir = modoMultiplo ? tagsDisponiveis : tagsDisponiveisFoto;
  const isLoading = modoMultiplo ? loading : loadingFoto;
  const isLoadingAcao = modoMultiplo ? loadingAcao : loadingAcaoFoto;

  // Verifica se o termo digitado já existe nas tags
  const tagJaExiste = tagsParaExibir.some(
    t => t.nome.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Calcula posição do popover - centralizado no mobile
  const popoverStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 32px)',
        maxWidth: '320px',
        zIndex: 9999
      }
    : position
      ? {
          position: 'fixed',
          left: Math.min(Math.max(16, position.x - 160), window.innerWidth - 336),
          top: Math.min(Math.max(16, position.y), window.innerHeight - 450),
          zIndex: 9999
        }
      : {
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999
        };

  return (
    <>
      {/* Backdrop semi-transparente */}
      <div
        className="fixed inset-0 bg-black/40 z-[9998]"
        onClick={onClose}
      />

      {/* Popover */}
      <div
        ref={popoverRef}
        style={popoverStyle}
        className={`w-80 max-h-[70vh] rounded-xl shadow-2xl border overflow-hidden flex flex-col ${
          isDarkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <Tag className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`font-ageo-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {modoMultiplo ? `Tags (${fotosParaTaggear.length} fotos)` : 'Tags da Foto'}
            </span>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors ${
              isDarkMode
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Thumbnails das fotos (modo múltiplo ou única) */}
        {(modoMultiplo && fotosParaTaggear.length > 0) || fotoUrl ? (
          <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {modoMultiplo ? (
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2 overflow-hidden">
                  {fotosParaTaggear.slice(0, 5).map((foto, idx) => (
                    foto.url ? (
                      <img
                        key={foto.id}
                        src={foto.url}
                        alt={`Foto ${idx + 1}`}
                        className="w-10 h-10 object-cover rounded-lg border-2 border-white dark:border-gray-800"
                      />
                    ) : (
                      <div
                        key={foto.id}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-800'
                            : 'bg-gray-100 border-white'
                        }`}
                      >
                        <Image className="w-5 h-5 opacity-50" />
                      </div>
                    )
                  ))}
                </div>
                {fotosParaTaggear.length > 5 && (
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    +{fotosParaTaggear.length - 5}
                  </span>
                )}
              </div>
            ) : fotoUrl && (
              <img
                src={fotoUrl}
                alt="Foto selecionada"
                className="w-16 h-16 object-cover rounded-lg"
              />
            )}
          </div>
        ) : null}

        {/* Tags já adicionadas à foto (apenas modo único) */}
        {!modoMultiplo && tagsFoto.length > 0 && (
          <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-xs font-ageo-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Tags desta foto:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tagsFoto.map(tag => (
                <span
                  key={tag.id}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-ageo-medium ${
                    isDarkMode
                      ? 'bg-blue-900/50 text-blue-300'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {tag.nome}
                  <button
                    onClick={() => removerFoto(tag.id)}
                    disabled={isLoadingAcao}
                    className={`p-0.5 rounded-full transition-colors ${
                      isDarkMode
                        ? 'hover:bg-blue-800 text-blue-400'
                        : 'hover:bg-blue-200 text-blue-600'
                    }`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Aviso modo múltiplo */}
        {modoMultiplo && (
          <div className={`px-4 py-2 text-xs ${isDarkMode ? 'text-amber-400 bg-amber-900/20' : 'text-amber-600 bg-amber-50'}`}>
            As tags serão adicionadas em todas as {fotosParaTaggear.length} fotos selecionadas
          </div>
        )}

        {/* Input de busca/criação */}
        <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <Tag className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => { inputTouchedRef.current = true; setInputValue(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder="Buscar ou criar tag..."
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDarkMode
                  ? 'text-white placeholder-gray-500'
                  : 'text-gray-900 placeholder-gray-400'
              }`}
            />
            {isLoadingAcao && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
          </div>

          {/* Botão criar nova tag */}
          {inputValue.trim() && !tagJaExiste && (
            <button
              onClick={handleCriarTag}
              disabled={isLoadingAcao}
              className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-ageo-medium transition-colors ${
                isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <Plus className="w-4 h-4" />
              Criar tag "{inputValue.trim()}"
            </button>
          )}
        </div>

        {/* Lista de tags disponíveis */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className={`w-6 h-6 animate-spin ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
          ) : erro ? (
            <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
              {erro}
            </div>
          ) : tagsParaExibir.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {inputValue ? 'Nenhuma tag encontrada' : 'Nenhuma tag criada ainda'}
              </p>
              <p className="text-xs mt-1">
                Digite acima para criar uma nova tag
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {tagsParaExibir.map(tag => {
                const selecionada = !modoMultiplo && temTag(tag.id);
                return (
                  <div
                    key={tag.id}
                    className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      selecionada
                        ? isDarkMode
                          ? 'bg-blue-900/40 text-blue-300'
                          : 'bg-blue-50 text-blue-700'
                        : isDarkMode
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <button
                      onClick={() => handleToggleTag(tag.id, tag.nome)}
                      disabled={isLoadingAcao}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      {!modoMultiplo && (
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selecionada
                            ? isDarkMode
                              ? 'bg-blue-600 border-blue-600'
                              : 'bg-blue-500 border-blue-500'
                            : isDarkMode
                              ? 'border-gray-600'
                              : 'border-gray-300'
                        }`}>
                          {selecionada && <Check className="w-3 h-3 text-white" />}
                        </div>
                      )}
                      {modoMultiplo && (
                        <Plus className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      )}
                      <span className="flex-1 text-sm font-ageo-regular truncate">
                        {tag.nome}
                      </span>
                      {tag.uso_count > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {tag.uso_count}
                        </span>
                      )}
                    </button>
                    {/* Botão excluir - aparece no hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTagParaExcluir(tag);
                      }}
                      disabled={isLoadingAcao}
                      className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDarkMode
                          ? 'hover:bg-red-900/50 text-red-400'
                          : 'hover:bg-red-100 text-red-500'
                      }`}
                      title="Excluir tag"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {tagParaExcluir && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[10000]"
            onClick={() => !excluindo && setTagParaExcluir(null)}
          />
          <div
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[10001] w-80 rounded-xl shadow-2xl border p-4 ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-full ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                <Trash2 className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
              </div>
              <div>
                <h3 className={`font-ageo-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Excluir tag
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>

            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Tem certeza que deseja excluir a tag <strong>"{tagParaExcluir.nome}"</strong>?
              {tagParaExcluir.uso_count > 0 && (
                <span className={`block mt-1 text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  Esta tag está sendo usada em {tagParaExcluir.uso_count} foto(s).
                </span>
              )}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => setTagParaExcluir(null)}
                disabled={excluindo}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-ageo-medium transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluirTag}
                disabled={excluindo}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-ageo-medium text-white transition-colors flex items-center justify-center gap-2 ${
                  excluindo
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {excluindo ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Excluir'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
